import axios, { AxiosHeaders } from "axios";
import config from "../../config/";
import {
  AdminUpdateCompanyUserRequest,
  AgreeToAgreementResponseRequest,
  AgreeToAgreementResponseResponse,
  ChangePasswordRequest,
  ChangePasswordResponse,
  CreateAgreementCampaignRequest,
  CreateAgreementCampaignResponse,
  CreateCompanyRequest,
  CreateSurveyCampaignRequest,
  CreateSurveyCampaignResponse,
  CreateSurveyTemplateRequest,
  DeleteSurveyCampaignRequest,
  GetAgreementCampaignByIDRequest,
  GetAgreementCampaignByIDResponse,
  GetAgreementCampaignsResponse,
  GetAgreementResponsesByAgreementCampaignIDRequest,
  GetAgreementResponsesByAgreementCampaignIDResponse,
  GetAllCompanyUserRequest,
  GetAllCompanyUserResponse,
  GetCompaniesResponse,
  GetCompanyUsersByCompanyIdRequest,
  GetCompanyUsersByCompanyIdResponse,
  GetConstantsRequest,
  GetConstantsResponse,
  GetEmailTemplateByCompanyIdRequest,
  GetEmailTemplateByCompanyIdResponse,
  GetEmailTemplateByEmailTemplateIdRequest,
  GetEmailTemplateByEmailTemplateIdResponse,
  GetEmailTemplateVariablesRequest,
  GetEmailTemplateVariablesResponse,
  GetFeatureFlagsByCompanyIdRequest,
  GetFeatureFlagsByCompanyIdResponse,
  GetProfileResponse,
  GetReportRequest,
  GetSurveyCampaignBasicInfoRequest,
  GetSurveyCampaignBasicInfoResponse,
  GetSurveyCampaignBySurveyCampaignIdRequest,
  GetSurveyCampaignBySurveyCampaignIdResponse,
  GetSurveyCampaignsRequest,
  GetSurveyCampaignsResponse,
  GetSurveyResponseBySurveyCampaignIdRequest,
  GetSurveyResponseBySurveyCampaignIdResponse,
  GetSurveyResponseBySurveyResponseIdRequest,
  GetSurveyResponseBySurveyResponseIdResponse,
  GetSurveyResponseDetailRequest,
  GetSurveyResponseDetailResponse,
  GetSurveyTemplatesAdminResponse,
  GetSurveyTemplatesBySurveyTemplateIdRequest,
  GetSurveyTemplatesBySurveyTemplateIdResponse,
  GetSurveyTemplatesRequest,
  GetSurveyTemplatesResponse,
  GetUploadTncFilePresignedUrlRequest,
  GetUploadTncFilePresignedUrlResponse,
  IsResetPasswordCodeValidRequest,
  IsResetPasswordCodeValidResponse,
  LoginRequest,
  LoginResponse,
  RefreshAccessTokenResponse,
  ResendSurveyResponseEmailRequest,
  ResendSurveyResponseEmailResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
  SaveAsDraftUpdateSurveyCampaignEmployeesRequest,
  SaveSurveyResponseAsDraftRequest,
  SaveSurveyResponseAsDraftResponse,
  SendResetPasswordEmailRequest,
  SignUpRequest,
  SignUpResponse,
  UndeleteSurveyCampaignRequest,
  UndeleteSurveyCampaignResponse,
  UpdateCompanyRequest,
  UpdateCompanyUserRequest,
  UpdateEmailTemplateRequest,
  UpdateEmailTemplateResponse,
  UpdateFeatureFlagStatusByCompanyIdRequest,
  UpdateFeatureFlagStatusByCompanyIdResponse,
  UpdateSurveyCampaignBasicRequest,
  UpdateSurveyCampaignEmployeesRequest,
  UploadTncFileRequest,
  UploadTncFileResponse,
  ValidateUpdateSurveyCampaignEmployeesRequest
} from "../../constants/apiRequestResponse";
import {
  LOCAL_STORAGE_KEYS,
  getAccessToken,
  getRefreshToken,
  getRefreshingTokenLock,
  removeAccessToken,
  removeRefreshToken,
  removeRefreshingTokenLock,
  setAccessToken,
  setRefreshToken,
  setRefreshingTokenLock,
} from "../local";


function getHeadersWithoutAuth() {
  return {
    "Content-Type": "application/json",
    Accept: "application/json, text/html",
  };
}

const instance = axios.create({
  baseURL: config.api,
});
instance.interceptors.request.use(
  async (request) => {
    let token = getAccessToken();
    if (!token) return request;

    const jwt = JSON.parse(atob(token.split(".")[1]));

    const { exp } = jwt;

    const now = Math.floor(Date.now() / 1000);
    const BUFFER_IN_SECONDS = 5;
    const expWithBuffer = exp - BUFFER_IN_SECONDS;

    if (now >= expWithBuffer) {
      console.log(
        "Token is expired. Attempting to refresh access token via refresh token..."
      );

      const refreshingTokenLock = getRefreshingTokenLock();

      /**
       * 8/3/2023 daniel.kwok
       * Preventing race condition when there are multiple, simultaneous request to refresh access token
       */
      if (!refreshingTokenLock) {
        await setRefreshingTokenLock();

        await refreshAccessToken()
          .then((res) => {
            if (!res.success) throw res.message;
            setAccessToken(res.accessToken);
            setRefreshToken(res.refreshToken);
          })
          .catch((err) => {
            removeAccessToken();
            removeRefreshToken();
          });

        await removeRefreshingTokenLock();
      }
    }

    token = getAccessToken();
    request.headers.Authorization = `Bearer ${token}`;

    return request;
  },
  function (error) {
    console.log(`Request errored`);
    return Promise.reject(error);
  }
);

instance.interceptors.response.use(
  function (response) {
    return response;
  },
  function (error) {
    /**
     * if there are query params, window.location.pathname would include a trailing
     * slash at the back of path.
     * Hence need to take into account path with & without trailing slash ¯\_(ツ)_/¯
     */
    const pathsOmittedFromRedirects = [
      "/login",
      "/reset-password",
      "/login/",
      "/reset-password/",
    ];

    if (error?.response?.status === 401) {
      if (!pathsOmittedFromRedirects.includes(window.location.pathname)) {
        window.location.href = `/login?redirect=${window.location.pathname}${window.location.search}`;
      }
    }

    // TODO pass the entire response back, allow the API caller to decide what to do instead of this
    return Promise.reject(
      error?.response?.data?.details ||
      error?.response?.data?.message ||
      error?.response?.data ||
      `Something went wrong. Try again?`
    );
  }
);

export function fetcher(path: string) {
  return instance(path).then((res) => res.data);
}

function refreshAccessToken(): Promise<RefreshAccessTokenResponse> {
  const RT = getRefreshToken();

  return axios
    .post(`${config.api}/companyUser/access-token`, null, {
      headers: {
        [LOCAL_STORAGE_KEYS.REFRESH_TOKEN]: `Bearer ${RT}`,
      },
    })
    .then((res) => res.data);
}

export const login = (loginRequest: LoginRequest): Promise<LoginResponse> => {
  return axios.post(`${config.api}/companyUser/login`, loginRequest).then(res => res.data);
};

export const signup = (
  loginRequest: SignUpRequest
): Promise<SignUpResponse> => {
  return instance
    .post(`/admin/companyUser/signup`, loginRequest)
    .then((res) => res.data);
};

export const logout = () => {
  removeAccessToken();
  return true;
};

export const getProfile = (): Promise<GetProfileResponse> => {
  return instance.get(`/companyUser`).then((res) => res.data);
};

export const deleteUser = (companyUserId: string): Promise<void> => {
  return instance.delete(`/admin/companyUser/${companyUserId}`).then((res) => res.data);
};

export const getAllCompanyUsers = (
  req?: GetAllCompanyUserRequest
): Promise<GetAllCompanyUserResponse> => {
  return instance(`/admin/companyUser`).then((res) => res.data);
};

export const getCompanyUsersByCompanyId = (
  req?: GetCompanyUsersByCompanyIdRequest
): Promise<GetCompanyUsersByCompanyIdResponse> => {
  return instance
    .get(`/admin/companyUser/getByCompanyId/${req?.companyId}`)
    .then((res) => res.data);
};

export const changeCompanyUserPassword = (
  req?: ChangePasswordRequest
): Promise<ChangePasswordResponse> => {
  return instance
    .post("/companyUser/changePassword", req)
    .then((res) => res.data);
};

export const adminChangeCompanyUserPassword = (
  req: ChangePasswordRequest
): Promise<ChangePasswordResponse> => {
  return instance
    .post(`/admin/companyUser/changePassword`, req)
    .then((res) => res.data);
};

export const sendForgotPasswordEmail = (
  req?: SendResetPasswordEmailRequest
) => {
  return fetch(`${config.api}/companyUser/sendForgotPasswordEmail`, {
    method: "POST",
    headers: getHeadersWithoutAuth(),
    body: JSON.stringify(req),
  })
};

export const getIsResetPasswordTokenValid = (
  req?: IsResetPasswordCodeValidRequest
): Promise<IsResetPasswordCodeValidResponse> => {
  return fetch(`${config.api}/companyUser/isResetPasswordCodeValid`, {
    method: "POST",
    headers: getHeadersWithoutAuth(),
    body: JSON.stringify(req),
  }).then((res) => res.json());
};

export const resetPassword = (
  req?: ResetPasswordRequest
): Promise<ResetPasswordResponse> => {
  return instance
    .post("/companyUser/resetPassword", req)
    .then((res) => res.data);
};

export const adminUpdateCompanyUser = (
  req: AdminUpdateCompanyUserRequest
): Promise<void> => {
  return instance.put("/admin/companyUser", req).then((res) => res.data);
};

export const updateCompanyUser = (
  req: UpdateCompanyUserRequest
): Promise<void> => {
  return instance.put(`/companyUser`, req)
    .then(res => res.data)
};

export const createCompany = (req: CreateCompanyRequest): Promise<void> => {
  const formData = new FormData();
  Object.keys(req).forEach(key => {
    const value = req[key]
    if (value === undefined || value === null) return
    formData.append(key, req[key])
  })

  return instance.post(`/company`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    .then(res => res.data)
}

export const updateCompany = (
  req: UpdateCompanyRequest,
  companyId: string
): Promise<void> => {
  const formData = new FormData();
  Object.keys(req).forEach(key => {
    const value = req[key]
    if (value === null || value === undefined) return
    formData.append(key, req[key])
  })

  return instance.put(`/company/${companyId}`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    .then(res => res.data)
}

export const getAllCompanies = (): Promise<GetCompaniesResponse> => {
  return instance(`/company`).then((res) => res.data)
}

export const deleteCompany = (companyId: string): Promise<void> => {
  return instance.delete(`/company/${companyId}`).then((res) => res.data)
}

export const getSurveyTemplates = (
  req?: GetSurveyTemplatesRequest
): Promise<GetSurveyTemplatesResponse> => {
  return instance.get(`${config.api}/surveyTemplate`, {
    params: {
      name: req?.name,
      isGetDeleted: req?.isGetDeleted
    }
  }).then((res) => res.data);
};

export const getSurveyTemplateBySurveyTemplateId = (
  req?: GetSurveyTemplatesBySurveyTemplateIdRequest
): Promise<GetSurveyTemplatesBySurveyTemplateIdResponse> => {
  return instance(
    `/surveyTemplate/${req?.surveyTemplateId || ""}`
  ).then((res) => res.data);
};

export const getSurveyTemplatesGrouped = (ignoreCache: boolean): Promise<GetSurveyTemplatesAdminResponse> => {
  const headers: AxiosHeaders = new AxiosHeaders();
  if (ignoreCache) {
    headers.set('Cache-Control', 'no-cache');
  }

  return instance.get(`/surveyTemplate/grouped`, {
    headers: headers
  }).then((res) => res.data);
};

export const createSurveyTemplate = (
  req: CreateSurveyTemplateRequest
) => {
  return instance.post(`/surveyTemplate`, req);
};

export const createSurveyCampaign = (
  req: CreateSurveyCampaignRequest
): Promise<CreateSurveyCampaignResponse> => {
  return instance.post(`/surveyCampaign`, req).then((res) => res.data);
};

export const startSurveyCampaign = (
  surveyCampaignId: string
): Promise<undefined> => {
  return instance
    .post(`/surveyCampaign/start/${surveyCampaignId}`)
    .then((res) => res.data);
};

export const updateSurveyCampaignBasic = (
  req: UpdateSurveyCampaignBasicRequest,
  surveyCampaignId: string
): Promise<undefined> => {
  return instance
    .put(`/surveyCampaign/basic/${surveyCampaignId}`, req)
    .then((res) => res.data);
};

export const updateSurveyCampaignEmployees = (
  req: UpdateSurveyCampaignEmployeesRequest,
  surveyCampaignId: string
): Promise<undefined> => {
  return instance
    .put(`/surveyCampaign/employees/${surveyCampaignId}`, req)
    .then((res) => res.data);
};

export const validateUpdateSurveyCampaignEmployees = (
  req: ValidateUpdateSurveyCampaignEmployeesRequest,
  surveyCampaignId: string
): Promise<undefined> => {
  return instance
    .post(`/surveyCampaign/employees/validate/${surveyCampaignId}`, req)
    .then((res) => res.data);
};

export const saveAsDraftUpdateSurveyCampaignEmployees = (
  req: SaveAsDraftUpdateSurveyCampaignEmployeesRequest,
  surveyCampaignId: string
): Promise<undefined> => {
  return instance
    .put(`/surveyCampaign/employees/draft/${surveyCampaignId}`, req)
    .then((res) => res.data);
};

export const finishSurveyCampaign = (
  surveyCampaignId: string
): Promise<undefined> => {
  return instance
    .post(`/surveyCampaign/finish/${surveyCampaignId}`)
    .then((res) => res.data);
};

export const getSurveyCampaigns = (
  req: GetSurveyCampaignsRequest
): Promise<GetSurveyCampaignsResponse> => {
  const url = new URL(
    `${config.api}/surveyCampaign`
  );

  if (req.limit) {
    url.searchParams.set("limit", req?.limit?.toString());
  }
  if (req.status) {
    url.searchParams.set("status", req?.status?.toString());
  }
  if (req.skip) {
    url.searchParams.set("skip", req?.skip?.toString());
  }
  if (req.isGetDeleted) {
    url.searchParams.set("isGetDeleted", req?.isGetDeleted?.toString());
  }
  if (req.search) {
    url.searchParams.set("search", req?.search?.toString());
  }
  if (req.type) {
    url.searchParams.set("type", req?.type?.toString());
  }
  if (req.createdBefore) {
    url.searchParams.set("createdBefore", req?.createdBefore?.toString());
  }
  if (req.surveyTemplateId) {
    url.searchParams.set("surveyTemplateId", req?.surveyTemplateId?.toString());
  }
  if (req.email) {
    url.searchParams.set("email", req?.email?.toString());
  }
  return instance.get(url.toString()).then((res) => res.data);
};

export const getSurveyCampaignBySurveyCampaignId = (
  req: GetSurveyCampaignBySurveyCampaignIdRequest,
  ignoreCache = false
): Promise<GetSurveyCampaignBySurveyCampaignIdResponse> => {
  const headers: AxiosHeaders = new AxiosHeaders();
  if (ignoreCache) {
    headers.set('Cache-Control', 'no-cache');
  }
  return instance
    .get(`/surveyCampaign/getBySurveyCampaignId/${req?.surveyCampaignId || ""}`, {
      headers: headers
    })
    .then((res) => res.data);
};

export const deleteSurveyCampaign = (
  req: DeleteSurveyCampaignRequest
) => {
  return instance
    .delete(`/surveyCampaign/${req.surveyCampaignId}`)
};

export const undeleteSurveyCampaign = (
  req: UndeleteSurveyCampaignRequest
): Promise<UndeleteSurveyCampaignResponse> => {
  return instance.post(`/surveyCampaign/undelete/${req.surveyCampaignId}`, req).then((res) => res.data);
};

export const getSurveyResponsesBySurveyCampaignId = (
  req: GetSurveyResponseBySurveyCampaignIdRequest,
  ignoreCache = false
): Promise<GetSurveyResponseBySurveyCampaignIdResponse> => {
  const headers: AxiosHeaders = new AxiosHeaders();
  if (ignoreCache) {
    headers.set('Cache-Control', 'no-cache');
  }
  return instance(
    `/surveyResponse/getBySurveyCampaignId/${req?.surveyCampaignId || ""}`
    , {
      headers: headers
    }
  ).then((res) => res.data);
};

export const getSurveyResponseBySurveyResponseId = (
  req: GetSurveyResponseBySurveyResponseIdRequest
): Promise<GetSurveyResponseBySurveyResponseIdResponse> => {
  return instance(
    `/surveyResponse/getBySurveyResponseId/${req?.surveyResponseId || ""}`
  ).then((res) => res.data);
};

export const saveSurveyResponseAsDraft = (
  req: SaveSurveyResponseAsDraftRequest
): Promise<SaveSurveyResponseAsDraftResponse> => {
  return instance
    .post(`/surveyResponse/saveDraft`, req)
    .then((res) => res.data);
};

export const finishSurveyResponse = (
  req: SaveSurveyResponseAsDraftRequest
): Promise<SaveSurveyResponseAsDraftResponse> => {
  return instance.post(`/surveyResponse/finish`, req).then((res) => res.data);
};

export const resendSurveyResponseEmail = (
  req: ResendSurveyResponseEmailRequest
): Promise<ResendSurveyResponseEmailResponse> => {
  return instance.post(`/surveyResponse/resend`, req).then((res) => res.data);
};

export const getSurveyCampaignBasicInfo = (
  req: GetSurveyCampaignBasicInfoRequest
): Promise<GetSurveyCampaignBasicInfoResponse> => {
  return instance(
    `/surveyCampaign/getBasicInfo/${req?.surveyCampaignId || ""}`
  ).then((res) => res.data);
};

export const getEmailTemplatesByCompanyId = (
  req: GetEmailTemplateByCompanyIdRequest
): Promise<GetEmailTemplateByCompanyIdResponse> => {
  return instance(`/emailTemplate/get/${req?.companyId || ""}`).then(
    (res) => res.data
  );
};

export const getEmailTemplateByEmailTemplateId = (
  req: GetEmailTemplateByEmailTemplateIdRequest
): Promise<GetEmailTemplateByEmailTemplateIdResponse> => {
  return instance(
    `/emailTemplate/getByEmailTemplateId/${req?.emailTemplateId || ""}`
  ).then((res) => res.data);
};

export const getEmailTemplateVariables = (
  req: GetEmailTemplateVariablesRequest
): Promise<GetEmailTemplateVariablesResponse> => {
  return instance(`/emailTemplate/variables/get`).then((res) => res.data);
};

export const updateEmailTemplate = (
  req: UpdateEmailTemplateRequest
): Promise<UpdateEmailTemplateResponse> => {
  return instance.post(`/emailTemplate/update`, req).then((res) => res.data);
};

export const getConstants = (
  req: GetConstantsRequest
): Promise<GetConstantsResponse> => {
  return instance
    .get(
      `/constants?keys=${req.keys?.join(",") || ""}&parsed=${req.parsed
      }&surveyResponseId=${req.surveyResponseId || ""}`
    )
    .then((res) => res.data);
};
export const getSurveyResponseDetail = (
  req: GetSurveyResponseDetailRequest,
): Promise<GetSurveyResponseDetailResponse> => {

  return instance
    .get(`/surveyResponse/detail/${req.surveyResponseId || ""}`)
    .then((res) => res.data);
};

export const getAgreementCampaigns =
  (): Promise<GetAgreementCampaignsResponse> => {
    return instance.get(`/agreementCampaign`).then((res) => res.data);
  };

export const createAgreementCampaign = (
  req: CreateAgreementCampaignRequest
): Promise<CreateAgreementCampaignResponse> => {
  return instance.post(`/agreementCampaign`, req).then((res) => res.data);
};

export const uploadTncFile = (
  req: UploadTncFileRequest
): Promise<UploadTncFileResponse> => {
  return instance
    .post(`/agreementCampaign/uploadTncFile`, req)
    .then((res) => res.data);
};

export const getUploadTncFilePresignedUrl = (
  req: GetUploadTncFilePresignedUrlRequest
): Promise<GetUploadTncFilePresignedUrlResponse> => {
  return instance
    .get(`/agreementCampaign/uploadTncFilePresignedUrl/${req.filename}`)
    .then((res) => res.data);
};

export const getAgreementCampaignByID = (
  req: GetAgreementCampaignByIDRequest
): Promise<GetAgreementCampaignByIDResponse> => {
  return instance.get(`/agreementCampaign/${req.id}`).then((res) => res.data);
};

export const getAgreementResponsesByAgreementCampaignID = (
  req: GetAgreementResponsesByAgreementCampaignIDRequest
): Promise<GetAgreementResponsesByAgreementCampaignIDResponse> => {
  return instance
    .get(`/agreementResponse/${req.agreementCampaignId}`)
    .then((res) => res.data);
};

export const agreeToAgreementResponse = (
  req: AgreeToAgreementResponseRequest
): Promise<AgreeToAgreementResponseResponse> => {
  return instance.post(`/agreementResponse/agree`, req).then((res) => res.data);
};

export const getFeatureFlagsByCompanyId = (
  req: GetFeatureFlagsByCompanyIdRequest
): Promise<GetFeatureFlagsByCompanyIdResponse> => {
  return instance.get(`/featureFlag/${req.companyId}`).then((res) => res.data);
};

export const updateFeatureFlagStatusByCompanyId = (
  req: UpdateFeatureFlagStatusByCompanyIdRequest
): Promise<UpdateFeatureFlagStatusByCompanyIdResponse> => {
  return instance.put(`/featureFlag`, req).then((res) => res.data);
};

export const getReport = (req: GetReportRequest) => {
  const url = new URL(
    `${config.api}/surveyCampaign/report/v3/${req.surveyCampaignId}`
  );

  if (req?.format) {
    url.searchParams.set("format", req.format);
  }

  if (req?.layout) {
    url.searchParams.set("layout", req.layout);
  }

  if (req?.team) {
    url.searchParams.set("team", req.team);
  }

  if (req?.surveyResponseId) {
    url.searchParams.set("surveyResponseId", req.surveyResponseId);
  }

  if (req?.comparedTo) {
    url.searchParams.set("comparedTo", req.comparedTo);
  }

  return instance
    .get(url.toString(), {
      responseType: req.format === "pdf" ? "blob" : undefined,
    })
    .then((res) => {
      if (req.format !== "pdf") {
        return res;
      }
      // It is necessary to create a new blob object with mime-type explicitly set
      // otherwise only Chrome works like it should
      var newBlob = new Blob([res.data], { type: "application/pdf" });

      // For other browsers:
      // Create a link pointing to the ObjectURL containing the blob.
      const data = window.URL.createObjectURL(newBlob);
      var link = document.createElement("a");
      link.href = data;
      const fileName = getFilenameFromHeader(res.headers["content-type"]);
      link.download = fileName || "report.pdf";
      link.click();
      setTimeout(function () {
        // For Firefox it is necessary to delay revoking the ObjectURL
        window.URL.revokeObjectURL(data);
      }, 100);
    });

  function getFilenameFromHeader(header: string) {
    if (!header) return null;

    const filenameRegex = /filename\*=UTF-8''(.+)|filename="(.+)"/i;
    const matches = header.match(filenameRegex);

    if (matches) {
      // If the filename is encoded as UTF-8
      if (matches[1]) {
        return decodeURIComponent(matches[1]);
      }
      // If the filename is plain text
      if (matches[2]) {
        return matches[2];
      }
    }
    return null;
  }
};
